import { useState, useEffect, useCallback } from 'react';
import { X, FileText, CheckCircle } from 'lucide-react';
import LeadMagnetModal from '@/components/landing/LeadMagnetModal';

export default function ExitIntentPopup() {
  const [showPopup, setShowPopup] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);

  const handleExitIntent = useCallback((e: MouseEvent) => {
    // Only trigger on mouse leaving toward top of viewport (browser chrome)
    if (e.clientY <= 5 && !hasTriggered) {
      // Check if user has dismissed before (stored in localStorage)
      const dismissed = localStorage.getItem('exitIntentDismissed');
      if (!dismissed) {
        setShowPopup(true);
        setHasTriggered(true);
      }
    }
  }, [hasTriggered]);

  useEffect(() => {
    // Don't show on mobile (no mouse exit intent)
    if (window.innerWidth < 768) return;

    // Delay adding listener to avoid triggering on page load
    const timeout = setTimeout(() => {
      document.addEventListener('mouseleave', handleExitIntent);
    }, 5000); // Wait 5 seconds before enabling

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mouseleave', handleExitIntent);
    };
  }, [handleExitIntent]);

  const handleDismiss = () => {
    setShowPopup(false);
    localStorage.setItem('exitIntentDismissed', 'true');
  };

  const handleGetPlaybook = () => {
    setShowPopup(false);
    setShowLeadModal(true);
  };

  if (!showPopup) {
    return (
      <LeadMagnetModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        type="playbook"
      />
    );
  }

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        className="fixed inset-0 bg-black/50 z-[60] animate-fade-in cursor-default"
        onClick={handleDismiss}
        aria-label="Close popup"
      />

      {/* Popup */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[61] w-full max-w-md mx-4 animate-scale-in">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 relative">
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-1 text-white/70 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  Before you go...
                </h3>
                <p className="text-sm text-white/80">
                  Get our free Engineering Metrics Playbook
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Learn how to roll out engineering metrics without creating a surveillance culture.
              Built on our 803K+ pull request study and how real teams used it.
            </p>

            <ul className="space-y-2 mb-6">
              {[
                'Which metrics matter (and which are toxic)',
                'How to communicate metrics to your team',
                '90-day implementation roadmap',
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleGetPlaybook}
                className="w-full px-5 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Get the Free Playbook
              </button>
              <button
                onClick={handleDismiss}
                className="w-full px-5 py-2 text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                No thanks, I'll figure it out myself
              </button>
            </div>
          </div>
        </div>
      </div>

      <LeadMagnetModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        type="playbook"
      />
    </>
  );
}
